import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
export default function CSS_ReferenceCombinators() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔗 CSS Combinators</h1>

      {/* Intro */}
      <p className="tutorial-intro" style={{ marginBottom: "30px" }}>
        CSS <strong>Combinators</strong> define the relationship between two
        selectors. They allow you to select elements based on how they are
        nested or positioned relative to others.
      </p>
      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>Combinators define relationships between selectors.</li>
          <li>
            <strong>Descendant (space):</strong> Any nested element inside
            another.
          </li>
          <li>
            <strong>Child (&gt;):</strong> Only direct child elements.
          </li>
          <li>
            <strong>Adjacent (+):</strong> Immediate next sibling only.
          </li>
          <li>
            <strong>General (~):</strong> All following siblings with same
            parent.
          </li>
        </ul>
      </div>

      {/* 1️⃣ Descendant Combinator */}
      <h2 className="section-title">1️⃣ Descendant Combinator (space)</h2>
      <p>
        Selects elements that are <strong>inside</strong> another element (at
        any level of nesting).
      </p>
      <CodeCopyBlock
        language="css"
        code={`div p {
  color: blue;
}`}
      />
      <p style={{ marginBottom: "30px" }}>
        👉 All <code>&lt;p&gt;</code> elements inside any{" "}
        <code>&lt;div&gt;</code> will be blue.
      </p>

      {/* 2️⃣ Child Combinator */}
      <h2 className="section-title">2️⃣ Child Combinator (&gt;)</h2>
      <p>
        Selects elements that are <strong>direct children</strong> of another
        element.
      </p>
      <CodeCopyBlock
        language="css"
        code={`div > p {
  color: green;
}`}
      />
      <p style={{ marginBottom: "30px" }}>
        👉 Only <code>&lt;p&gt;</code> elements directly inside a{" "}
        <code>&lt;div&gt;</code> are affected.
      </p>

      {/* 3️⃣ Adjacent Sibling Combinator */}
      <h2 className="section-title">3️⃣ Adjacent Sibling Combinator (+)</h2>
      <p>
        Selects an element that is the <strong>immediately next sibling</strong>{" "}
        of a specified element.
      </p>
      <CodeCopyBlock
        language="css"
        code={`h2 + p {
  font-weight: bold;
}`}
      />
      <p style={{ marginBottom: "30px" }}>
        👉 The <code>&lt;p&gt;</code> immediately following an{" "}
        <code>&lt;h2&gt;</code> becomes bold.
      </p>

      {/* 4️⃣ General Sibling Combinator */}
      <h2 className="section-title">4️⃣ General Sibling Combinator (~)</h2>
      <p>
        Selects elements that are <strong>siblings</strong> of a specified
        element (not necessarily immediately next).
      </p>
      <CodeCopyBlock
        language="css"
        code={`h2 ~ p {
  color: red;
}`}
      />
      <p style={{ marginBottom: "30px" }}>
        👉 All <code>&lt;p&gt;</code> elements after an <code>&lt;h2&gt;</code>{" "}
        (same parent) turn red.
      </p>

      {/* Example Structure */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h3 style={{ color: "#1e3a8a" }}>📘 Example Structure:</h3>
        <CodeCopyBlock
          language="html"
          code={`<div>
  <h2>Heading</h2>
  <p>Paragraph 1</p>
  <p>Paragraph 2</p>
</div>`}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #dcfce7, #bbf7d0)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#166534" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Move on to <strong>CSS Attribute Selectors</strong> to target
            elements by their attributes.
          </li>
          <li>
            Combine combinators with pseudo-classes like <code>:hover</code> or{" "}
            <code>:nth-child()</code>.
          </li>
          <li>Practice writing nested selectors for complex page layouts.</li>
        </ul>
      </div>
    </div>
  );
}
